"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/components/lib/utils"

// Body class management for preventing cursor issues
const useBodyClassEffect = (isOpen: boolean) => {
  React.useEffect(() => {
    if (isOpen) {
      // Add modal-open class when dialog opens
      document.body.classList.add('modal-open');
    } else {
      // Remove modal-open class when dialog closes with a small delay
      const timer = setTimeout(() => {
        document.body.classList.remove('modal-open');
        // Force remove any lingering pointer-events styles
        document.body.style.pointerEvents = '';
        document.body.style.cursor = '';
      }, 100);
      
      return () => clearTimeout(timer);
    }
    
    return () => {
      // Cleanup on unmount
      document.body.classList.remove('modal-open');
      document.body.style.pointerEvents = '';
      document.body.style.cursor = '';
    };
  }, [isOpen]);
};

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const dialogOverlayVariants = cva(
  "fixed inset-0 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
  {
    variants: {
      variant: {
        default: "bg-black/60 backdrop-blur-sm",
        dark: "bg-black/80",
        light: "bg-white/60 backdrop-blur-sm",
        blur: "bg-black/40 backdrop-blur-xl",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
)

export interface DialogOverlayProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>,
    VariantProps<typeof dialogOverlayVariants> {}

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, variant, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(dialogOverlayVariants({ variant, className }))}
    style={{
      zIndex: (props && props.style && (props.style as React.CSSProperties).zIndex) != null ? (props.style as React.CSSProperties).zIndex as number : 999999,
      ...(props.style as React.CSSProperties)
    } as React.CSSProperties}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const dialogContentVariants = cva(
  "fixed left-[50%] top-[50%] grid w-full translate-x-[-50%] translate-y-[-50%] gap-4 border shadow-2xl duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
  {
    variants: {
      variant: {
        default: "bg-background border-border rounded-xl",
        glass: "bg-background/80 backdrop-blur-xl border-white/20 dark:border-white/10 rounded-2xl",
        elevated: "bg-card border-border/50 rounded-2xl shadow-elevated-lg",
      },
      size: {
        default: "max-w-lg p-6",
        sm: "max-w-md p-5",
        lg: "max-w-2xl p-8",
        xl: "max-w-4xl p-8",
        full: "max-w-[95vw] max-h-[90vh] p-6 overflow-y-auto",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface DialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof dialogContentVariants> {
  hideCloseButton?: boolean;
  overlayVariant?: DialogOverlayProps["variant"];
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, variant, size, hideCloseButton, overlayVariant, ...props }, ref) => {
  // Use body class management to prevent cursor issues
  const [isOpen, setIsOpen] = React.useState(false);
  
  React.useEffect(() => {
    const element = ref && 'current' in ref ? ref.current : null;
    if (element) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-state') {
            const state = element.getAttribute('data-state');
            setIsOpen(state === 'open');
          }
        });
      });
      
      observer.observe(element, { attributes: true });
      
      // Set initial state
      const initialState = element.getAttribute('data-state');
      setIsOpen(initialState === 'open');
      
      return () => observer.disconnect();
    }
  }, [ref]);
  
  useBodyClassEffect(isOpen);
  
  return (
    <DialogPortal>
      <DialogOverlay variant={overlayVariant} />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(dialogContentVariants({ variant, size, className }))}
        style={{
          ...(props.style as React.CSSProperties),
          zIndex: (props && props.style && (props.style as React.CSSProperties).zIndex) != null ? (props.style as React.CSSProperties).zIndex as number : 1000000
        } as React.CSSProperties}
        {...props}
      >
        {children}
        {!hideCloseButton && (
          <DialogPrimitive.Close className="absolute right-4 top-4 w-8 h-8 rounded-xl flex items-center justify-center text-slate-500 transition-all hover:text-slate-700 hover:bg-slate-100 focus:outline-none active:scale-95 disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col space-y-2 text-center sm:text-left",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-4",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-xl font-semibold leading-none tracking-tight text-foreground",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  dialogOverlayVariants,
  dialogContentVariants,
}
